import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Check, ChevronDown, Globe, LogOut, Menu, Moon, Search, Settings, Sun, UserCircle, X
} from 'lucide-react';
import { PERMISSIONS, TERMINAL_KIND_LABELS, terminalShowsNav } from '@erp/shared';
import { useAuth } from '@/lib/auth';
import { setLocale } from '@/lib/i18n';
import { useTheme } from '@/lib/theme';
import { cn, initials } from '@/lib/utils';
import { useEventStream } from '@/hooks/useEventStream';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown';
import { stationVisibleFor, visibleNav } from './navigation';
import { ShellContext } from './shell-context';
import { ImpersonationBanner } from './ImpersonationBanner';

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { user, terminal, session, can, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  useEventStream(Boolean(session));

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const showAdmin = useMemo(
    () => terminalShowsNav(terminal?.kind, 'admin')
      && [PERMISSIONS.USERS_MANAGE, PERMISSIONS.AUDIT_READ, PERMISSIONS.SETTINGS_MANAGE].some((p) => can(p)),
    [can, terminal?.kind]
  );

  // Misma regla que el armazón de puesto: permiso del rol y alcance del terminal. Las pantallas
  // de puesto (el escaneo del almacén) solo se ofrecen a quien tiene ese rol: al administrador
  // le sobran en su menú, y un almacenero en una sesión de oficina las necesita.
  const esDeAlmacen = stationVisibleFor(user?.roles);
  const groups = useMemo(
    () => visibleNav(can, terminal?.kind, { stationVisible: esDeAlmacen }),
    [can, terminal?.kind, esDeAlmacen]
  );

  const isActive = (to: string) => to === '/'
    ? location.pathname === '/'
    : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = search.trim();
    if (!value) return;
    navigate(`/products?q=${encodeURIComponent(value)}`);
    setSearch('');
    setMobileOpen(false);
  };

  return (
    <ShellContext.Provider value={{ variant: 'desktop' }}>
    <div className="min-h-full bg-background">
      <ImpersonationBanner />
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1700px] items-center gap-3 px-4 lg:px-6">
          <button type="button" className="flex shrink-0 items-center gap-2.5" onClick={() => navigate('/')}>
            <div className="flex size-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-teal-400 font-bold text-white shadow-lg shadow-blue-500/20">E</div>
            <div className="hidden text-left sm:block">
              <p className="whitespace-nowrap text-sm font-semibold leading-4 text-foreground">Eurobath <span className="text-primary">ERP</span></p>
              <p className="whitespace-nowrap text-[10px] leading-3 text-muted-foreground">Fase 1 · LED Mirrors</p>
            </div>
          </button>

          <nav className="ml-2 hidden min-w-0 items-center gap-0.5 overflow-x-auto lg:flex">
            {groups.map((group) => {
              const active = group.items.some((item) => isActive(item.to));
              return (
                <DropdownMenu key={group.labelKey}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-[13px] font-medium transition',
                        active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      {t(group.labelKey)}
                      <ChevronDown className="size-3.5 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-60">
                    <DropdownMenuLabel>{t(group.labelKey)}</DropdownMenuLabel>
                    {group.items.map((item) => (
                      <DropdownMenuItem key={item.to} onSelect={() => navigate(item.to)} className={cn(isActive(item.to) && 'bg-muted')}>
                        <item.icon className="size-4 text-muted-foreground" />
                        <span className="flex-1">{t(item.labelKey)}</span>
                        {isActive(item.to) ? <Check className="size-3.5 text-primary" /> : null}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </nav>

          <form onSubmit={submitSearch} className="ml-auto hidden w-56 xl:block xl:w-64">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar artículo o SKU…" className="h-8 pl-8 text-xs" />
            </div>
          </form>

          <div className="ml-auto flex items-center gap-1.5 md:ml-0">
            {terminal ? (
              <div className="hidden shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-border bg-muted/40 px-2 py-1.5 lg:flex" title={`${terminal.code} · ${terminal.name}`}>
                <span className="size-1.5 rounded-full bg-emerald-500" />
                <span className="font-mono text-[10px] font-medium text-muted-foreground">{terminal.code.replace(/^TERMINAL-/, '')}</span>
                <span className="text-[10px] text-muted-foreground">· {TERMINAL_KIND_LABELS[terminal.kind as keyof typeof TERMINAL_KIND_LABELS] ?? terminal.kind}</span>
              </div>
            ) : null}

            <Button variant="ghost" size="icon-sm" aria-label={t('actions.language')} onClick={() => setLocale(i18n.language === 'es' ? 'en' : 'es')}>
              <Globe />
            </Button>
            <Button variant="ghost" size="icon-sm" aria-label={t('actions.theme')} onClick={toggle}>
              {theme === 'dark' ? <Sun /> : <Moon />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="ml-1 flex items-center gap-2 rounded-md p-1 pr-1.5 transition hover:bg-muted">
                  <span className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                    {initials(user?.fullName ?? 'EB')}
                  </span>
                  <span className="hidden text-left sm:block">
                    <span className="block max-w-28 truncate text-[11px] font-medium leading-3 text-foreground">{user?.fullName}</span>
                    <span className="block max-w-28 truncate text-[10px] leading-3 text-muted-foreground">{user?.roles[0]?.name}</span>
                  </span>
                  <ChevronDown className="hidden size-3.5 text-muted-foreground sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuItem onSelect={() => navigate('/account')}>
                  <UserCircle /> Mi cuenta y contraseña
                </DropdownMenuItem>
                {showAdmin ? (
                  <DropdownMenuItem onSelect={() => navigate('/admin')}>
                    <Settings /> Administración
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void logout()} className="text-destructive">
                  <LogOut /> Cerrar sesión / cambiar terminal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="icon-sm" className="lg:hidden" onClick={() => setMobileOpen((value) => !value)} aria-label="Menú">
              {mobileOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>

        {mobileOpen ? (
          <div className="border-t border-border bg-card lg:hidden">
            <div className="mx-auto max-w-[1700px] space-y-4 p-4">
              <form onSubmit={submitSearch} className="md:hidden">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar artículo…" className="h-9 pl-8 text-xs" />
                </div>
              </form>
              {groups.map((group) => (
                <div key={group.labelKey}>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{t(group.labelKey)}</p>
                  <div className="grid gap-0.5 sm:grid-cols-2">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === '/'}
                        className={({ isActive: activeLink }) => cn(
                          'flex items-center gap-2 rounded-md px-2.5 py-2 text-sm',
                          activeLink ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                        )}
                      >
                        <item.icon className="size-4" /> {t(item.labelKey)}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </header>

      <main className="mx-auto max-w-[1700px] p-4 lg:p-6">
        <div className="animate-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
    </ShellContext.Provider>
  );
}
